/**
 * A staff list from a spreadsheet: email, name, roles, departments, title.
 *
 * Pure. Takes the pasted or uploaded text and the organization's departments,
 * returns rows ready to send and rows that need fixing, with a reason each.
 * Role names may be the display names ("Administrator") or the ids
 * ("admin"), any case; departments are matched by name or code, any case.
 */

import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, type Role } from '../../types/roles';
import { splitName } from '../../types/personName';

export interface StaffRow {
  line: number;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: Role[];
  departmentIds?: string[];
  title?: string;
}

export interface StaffRowError { line: number; raw: string; reason: string }

export interface ParsedStaff { rows: StaffRow[]; errors: StaffRowError[] }

export const STAFF_CSV_HEADER = 'email,first name,last name,roles,departments,title';

/** A CSV cell: quoted when it holds a comma, a quote or a line break. */
function csvCell(v: string): string {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * The sample file to download, written against THIS organization's
 * departments so it imports without a single error. A sample naming a
 * department the hospital does not have taught people to expect errors.
 */
export function staffCsvSample(departments: ReadonlyArray<{ name: string }>): string {
  const [d1, d2] = departments.map((d) => d.name);
  const rows = [
    ['dana.reyes@hospital.org', 'Dana', 'Reyes', 'Author;Approver', d1 ?? '', 'Nurse manager'],
    ['sam.okafor@hospital.org', 'Sam', 'Okafor', 'Viewer', d2 ?? d1 ?? '', 'Charge nurse'],
    ['lee.park@hospital.org', 'Lee', 'Park', 'Administrator', '', 'Quality director'],
  ];
  return [STAFF_CSV_HEADER, ...rows.map((r) => r.map(csvCell).join(','))].join('\n') + '\n';
}

type Column = keyof StaffRow | 'departments' | 'fullName';

const HEADER_ALIASES: Record<string, Column> = {
  email: 'email', 'e-mail': 'email', 'email address': 'email',
  'first name': 'firstName', first: 'firstName', 'given name': 'firstName', firstname: 'firstName',
  'last name': 'lastName', last: 'lastName', surname: 'lastName', 'family name': 'lastName', lastname: 'lastName',
  // A sheet with one name column still imports; the name is split on its last space.
  name: 'fullName', 'full name': 'fullName', 'display name': 'fullName',
  roles: 'roles', role: 'roles',
  departments: 'departments', department: 'departments', dept: 'departments', unit: 'departments',
  title: 'title', 'job title': 'title', position: 'title',
};

/** Split one CSV line, honouring double quotes and doubled quotes. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

function roleFrom(text: string): Role | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  const byId = ASSIGNABLE_ROLES.find((r) => r === t);
  if (byId) return byId;
  const byName = ASSIGNABLE_ROLES.find((r) => ROLE_DISPLAY_NAMES[r].toLowerCase() === t);
  return byName ?? null;
}

export function parseStaffCsv(text: string, departments: readonly { id: string; name: string; code?: string | null }[]): ParsedStaff {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const rows: StaffRow[] = [];
  const errors: StaffRowError[] = [];
  const seen = new Set<string>();
  const deptByKey = new Map<string, string>();
  for (const d of departments) {
    deptByKey.set(d.name.trim().toLowerCase(), d.id);
    if (d.code) deptByKey.set(d.code.trim().toLowerCase(), d.id);
    deptByKey.set(d.id.toLowerCase(), d.id);
  }

  // The header row decides the columns; without one, the template's order.
  let columns: (Column | null)[] = ['email', 'firstName', 'lastName', 'roles', 'departments', 'title'];
  let start = 0;
  const first = lines.find((l) => l.trim() !== '');
  if (first !== undefined) {
    const cells = splitCsvLine(first).map((c) => c.toLowerCase());
    if (cells.some((c) => c === 'email' || c === 'e-mail' || c === 'email address')) {
      columns = cells.map((c) => HEADER_ALIASES[c] ?? null);
      start = lines.indexOf(first) + 1;
    }
  }

  for (let i = start; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    if (raw.trim() === '') continue;
    const line = i + 1;
    const cells = splitCsvLine(raw);
    const get = (key: Column) => {
      const idx = columns.indexOf(key);
      return idx === -1 ? '' : (cells[idx] ?? '');
    };
    const email = get('email').toLowerCase();
    if (!email.includes('@')) { errors.push({ line, raw, reason: 'No email address' }); continue; }
    if (seen.has(email)) { errors.push({ line, raw, reason: `${email} appears more than once` }); continue; }
    seen.add(email);

    const roleTexts = get('roles').split(/[;|/]/).map((s) => s.trim()).filter(Boolean);
    const roles: Role[] = [];
    let badRole: string | null = null;
    for (const t of roleTexts) {
      const r = roleFrom(t);
      if (!r) { badRole = t; break; }
      if (!roles.includes(r)) roles.push(r);
    }
    if (badRole) { errors.push({ line, raw, reason: `Unknown role "${badRole}" (use ${ASSIGNABLE_ROLES.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ')})` }); continue; }
    if (roles.length === 0) roles.push('viewer');

    const deptTexts = get('departments').split(/[;|/]/).map((s) => s.trim()).filter(Boolean);
    const departmentIds: string[] = [];
    let badDept: string | null = null;
    for (const t of deptTexts) {
      const id = deptByKey.get(t.toLowerCase());
      if (!id) { badDept = t; break; }
      if (!departmentIds.includes(id)) departmentIds.push(id);
    }
    if (badDept) { errors.push({ line, raw, reason: `Unknown department "${badDept}"` }); continue; }

    let firstName = get('firstName').trim();
    let lastName = get('lastName').trim();
    if (!firstName && !lastName) ({ firstName, lastName } = splitName(get('fullName')));
    const title = get('title');
    rows.push({
      line, email, roles,
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(departmentIds.length ? { departmentIds } : {}),
      ...(title ? { title } : {}),
    });
  }
  return { rows, errors };
}
